import { Document, Page, View, Text, StyleSheet, Font, Image } from "@react-pdf/renderer";
import persianFontRegular from "../../../assets/fonts/YekanBakh/woff/YekanBakh-Regular.woff";
import persianFontBold from "../../../assets/fonts/YekanBakh/woff/YekanBakh-Bold.woff";
import logoImage from "../../../assets/imgs/green logo.png";

Font.register({
    family: "iranSans",
    fonts: [
        { src: persianFontRegular, fontWeight: "normal" },
        { src: persianFontBold, fontWeight: "bold" },
    ],
});

const COLORS = {
    primary: "#1B4B91",
    primaryLight: "#EAF1FB",
    accent: "#0F9D58",
    text: "#1F2430",
    muted: "#7A8291",
    border: "#E4E7EC",
    stripe: "#FAFBFC",
};

const styles = StyleSheet.create({
    page: {
        direction: "rtl",
        fontFamily: "iranSans",
        backgroundColor: "#F1F3F6",
        padding: 20,
        fontSize: 9.5,
        color: COLORS.text,
    },

    ticket: {
        backgroundColor: "#FFFFFF",
        borderRadius: 10,
        overflow: "hidden",
        border: `1pt solid ${COLORS.border}`,
    },

    /* ---------- Header band ---------- */
    header: {
        display: "flex",
        flexDirection: "row-reverse",
        justifyContent: "space-between",
        alignItems: "center",
        paddingVertical: 10,
        paddingHorizontal: 15,
        borderBottom: `1.2pt solid ${COLORS.border}`,
    },
    headerTitle: {
        fontSize: 15,
        fontWeight: "bold",
    },
    headerSubtitle: {
        fontSize: 9,
        marginTop: 3,
    },
    logoImage: {
        width: 135,
        height: 30,
    },
    ticketNumberLabel: {
        fontSize: 7.5,
        marginBottom: 2,
    },
    ticketNumberValue: {
        fontSize: 11,
        fontWeight: "bold",
    },

    body: {
        padding: 20,
    },

    /* ---------- Route diagram ---------- */
    routeCard: {
        flexDirection: "row-reverse",
        alignItems: "center",
        marginBottom: 18,
    },
    routeCity: {
        width: 100,
    },
    routeCityName: {
        fontSize: 13,
        fontWeight: "bold",
        textAlign: "center",
    },
    routeCityLabel: {
        fontSize: 7.5,
        color: COLORS.muted,
        textAlign: "center",
        marginTop: 3,
    },
    routeLine: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        marginHorizontal: 6,
        position: "relative",
    },
    routeDot: {
        width: 7,
        height: 7,
        borderRadius: 3.5,
        backgroundColor: COLORS.primary,
    },
    routeDash: {
        flex: 1,
        height: 0,
        borderBottom: `1.2pt dashed ${COLORS.border}`,
        marginHorizontal: 4,
    },
    routeTimeBadge: {
        position: "absolute",
        left: "55%",
        top: -18,
        transform: "translateX(-50%)",
        borderRadius: 10,
        paddingVertical: 3,
        paddingHorizontal: 9,
    },
    routeTimeBadgeText: {
        fontSize: 8.5,
        fontWeight: "bold",
        color: COLORS.primary,
    },

    /* ---------- Info grid ---------- */
    infoGrid: {
        flexDirection: "row-reverse",
        backgroundColor: COLORS.stripe,
        borderRadius: 8,
        border: `1pt solid ${COLORS.border}`,
        marginBottom: 18,
    },
    infoCell: {
        flex: 1,
        flexDirection: "column-reverse",
        justifyContent: "center",
        alignItems: "center",
        paddingVertical: 10,
        paddingHorizontal: 12,
    },
    infoCellDivider: {
        borderLeft: `1pt solid ${COLORS.border}`,
    },
    infoLabel: {
        fontSize: 7.5,
        color: COLORS.muted,
        marginBottom: 4,
    },
    infoValue: {
        fontSize: 10,
        fontWeight: "bold",
    },

    /* ---------- Passengers table ---------- */
    titleRow: { flexDirection: "row-reverse", justifyContent: "flex-start" },
    sectionTitle: {
        fontSize: 10.5,
        fontWeight: "bold",
        marginBottom: 8,
    },
    table: {
        borderRadius: 8,
        overflow: "hidden",
        border: `1pt solid ${COLORS.border}`,
        marginBottom: 18,
    },
    tableHeaderRow: {
        flexDirection: "row-reverse",
        backgroundColor: COLORS.primaryLight,
        paddingVertical: 7,
        paddingHorizontal: 10,
    },
    tableHeaderCell: {
        fontSize: 8.5,
        fontWeight: "bold",
        color: COLORS.primary,
        textAlign: "right",
    },
    tableRow: {
        flexDirection: "row-reverse",
        paddingVertical: 7,
        paddingHorizontal: 10,
        borderTop: `1pt solid ${COLORS.border}`,
    },
    tableRowStriped: {
        backgroundColor: COLORS.stripe,
    },
    tableCell: {
        fontSize: 9,
        textAlign: "right",
    },
    colIndex: { width: 30 },
    colName: { flex: 2 },
    colNational: { flex: 1 },

    /* ---------- Price summary ---------- */
    priceRow: {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "center",
        marginBottom: 6,
        gap: 10,
    },
    priceLabel: {
        fontSize: 9,
        color: COLORS.text,
    },
    priceValue: {
        fontSize: 14,
        fontWeight: "bold",
        color: COLORS.accent,
    },

    /* ---------- Footer ---------- */
    perforation: {
        borderTop: `1.2pt dashed ${COLORS.border}`,
        marginTop: 4,
        marginBottom: 14,
    },
    barcodeRow: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "flex-end",
        gap: 1.5,
        marginBottom: 6,
    },
    footerNote: {
        fontSize: 7.5,
        color: COLORS.muted,
        textAlign: "center",
    },
});

export default function BusTicketPdfDocument({
    companyName,
    origin,
    destination,
    ticketDate,
    departureTime,
    ticketNumber,
    price,
    passengers = [],
    formatPrice = price => price,
}) {
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.ticket}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={[styles.infoCell, { alignItems: "flex-end" }]}>
                            <Text style={styles.headerTitle}>بلیط اتوبوس</Text>
                            <Text style={styles.headerSubtitle}>{companyName}</Text>
                        </View>
                        <View>
                            <Image src={logoImage} style={styles.logoImage} />
                        </View>
                        {ticketNumber && (
                            <View style={[styles.infoCell, { alignItems: "flex-start" }]}>
                                <Text style={styles.ticketNumberLabel}>شماره پیگیری</Text>
                                <Text style={styles.ticketNumberValue}>{ticketNumber}</Text>
                            </View>
                        )}
                    </View>

                    <View style={styles.body}>
                        {/* Route diagram */}
                        <View style={styles.routeCard}>
                            <View style={styles.routeCity}>
                                <Text style={styles.routeCityName}>{origin}</Text>
                                <Text style={styles.routeCityLabel}>مبدا</Text>
                            </View>

                            <View style={styles.routeLine}>
                                <View style={styles.routeDot} />
                                <View style={styles.routeDash} />
                                <View style={styles.routeTimeBadge}>
                                    <Text style={styles.routeTimeBadgeText}>{departureTime}</Text>
                                </View>
                                <View style={styles.routeDot} />
                            </View>

                            <View style={styles.routeCity}>
                                <Text style={styles.routeCityName}>{destination}</Text>
                                <Text style={styles.routeCityLabel}>مقصد</Text>
                            </View>
                        </View>

                        {/* Info grid */}
                        <View style={styles.infoGrid}>
                            <View style={[styles.infoCell, styles.infoCellDivider]}>
                                <Text style={styles.infoLabel}>تاریخ حرکت</Text>
                                <Text style={styles.infoValue}>{ticketDate}</Text>
                            </View>
                            <View style={[styles.infoCell, styles.infoCellDivider]}>
                                <Text style={styles.infoLabel}>ساعت حرکت</Text>
                                <Text style={styles.infoValue}>{departureTime}</Text>
                            </View>
                            <View style={styles.infoCell}>
                                <Text style={styles.infoLabel}>تعداد مسافر</Text>
                                <Text style={styles.infoValue}>{passengers.length} نفر</Text>
                            </View>
                        </View>

                        {/* Passengers table */}
                        <View style={styles.titleRow}>
                            <Text style={styles.sectionTitle}>مسافران</Text>
                        </View>
                        <View style={styles.table}>
                            <View style={styles.tableHeaderRow}>
                                <Text style={[styles.tableHeaderCell, styles.colIndex]}>ردیف</Text>
                                <Text style={[styles.tableHeaderCell, styles.colName]}>نام و نام خانوادگی</Text>
                                <Text style={[styles.tableHeaderCell, styles.colNational]}>کد ملی</Text>
                                <Text style={[styles.tableHeaderCell, styles.colNational]}>شماره صندلی</Text>
                            </View>

                            {passengers.map((passenger, index) => (
                                <View style={[styles.tableRow, index % 2 === 1 && styles.tableRowStriped]} key={`${passenger.last_name}-${index}`}>
                                    <Text style={[styles.tableCell, styles.colIndex]}>{index + 1}</Text>
                                    <Text style={[styles.tableCell, styles.colName]}>
                                        {passenger.first_name} {passenger.last_name}
                                    </Text>
                                    <Text style={[styles.tableCell, styles.colNational]}>{passenger.national_code || "-"}</Text>
                                    <Text style={[styles.tableCell, styles.colNational]}>{passenger.seat_number || "-"}</Text>
                                </View>
                            ))}
                        </View>

                        {/* Price summary */}
                        <View style={styles.priceRow}>
                            <Text style={styles.priceValue}>{formatPrice(price)}</Text>
                            <Text style={styles.priceLabel}>مبلغ کل (ریال)</Text>
                        </View>

                        {/* Footer */}
                        <View style={styles.perforation} />
                        <Text style={styles.footerNote}>.این برگه صرفاً جهت اطلاع صادر شده و جایگزین بلیط رسمی شرکت مسافربری نیست</Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
}
